import { useState } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, CheckCircle2, Inbox, RefreshCw, X } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { extractError } from '@/api/client';
import { formatMontant } from '@/utils/currency';
import { formatDateTimeShort } from '@/utils/date';
import { demandesApi, type Demande, type StatutDemande } from '@/api/demandes';
import { useFournisseurs } from '@/hooks/useBetonSprint5';

const LIBELLE_STATUT: Record<StatutDemande, string> = {
  soumise:       'À traiter',
  en_traitement: 'En cours',
  convertie:     'Commandée',
  refusee:       'Refusée',
};

const COULEUR_STATUT: Record<StatutDemande, string> = {
  soumise:       'ds-bd-w',
  en_traitement: 'ds-bd-i',
  convertie:     'ds-bd-s',
  refusee:       'ds-bd-d',
};

/**
 * SIMG-DEMANDE-TERRAIN-2026-001 — les demandes des chantiers, côté acheteur.
 *
 * Ce que le terrain envoie arrive ici. L'acheteur chiffre et convertit en bon
 * de commande — lequel part ensuite dans le circuit habituel, validation du
 * DAF comprise. Rien n'est engagé par la conversion.
 */
export const DemandesTerrainPage = () => {
  const qc = useQueryClient();
  const [statut, setStatut]   = useState<StatutDemande | ''>('');
  const [aTraiter, setATraiter] = useState<Demande | null>(null);
  const [erreur, setErreur]   = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const { data: demandes, isLoading, refetch } = useQuery({
    queryKey: ['achats', 'demandes', statut],
    queryFn:  () => demandesApi.lister(statut || undefined),
  });
  const { data: compteurs } = useQuery({
    queryKey: ['achats', 'demandes', 'compteurs'],
    queryFn:  demandesApi.compteurs,
  });

  const refuser = useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => demandesApi.refuser(id, motif),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['achats', 'demandes'] }),
  });

  const lignes = demandes ?? [];

  const refuserDemande = async (d: Demande) => {
    const motif = await demander.texte({
      titre: `Refuser ${d.reference} — pourquoi ?`,
      message: 'Le terrain lira ce motif. Sans explication, il rappellera.',
      libelle: 'Motif', min: 1, multiligne: true, confirmer: 'Refuser', danger: true,
    });
    if (!motif) return;
    setErreur(null);
    try {
      await refuser.mutateAsync({ id: d.id, motif: motif.trim() });
      setMessage(`${d.reference} refusée — le chantier en est informé.`);
    } catch (e) {
      setErreur(extractError(e).message);
    }
  };

  return (
    <>
      <Topbar
        title="Demandes du terrain"
        subtitle="Ce que les chantiers réclament — à chiffrer et convertir en bon de commande"
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        }
      />

      <div className="ds-content">
        {erreur && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{erreur}</div>
          </div>
        )}
        {message && (
          <div className="ds-alert-strip ds-alert-s" style={{ marginBottom: 16 }}>
            <div className="ds-alert-txt">{message}</div>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setMessage(null)}><X size={12} /></button>
          </div>
        )}

        {compteurs && (
          <div className="ds-kpi-grid" style={{ marginBottom: 20 }}>
            <Tuile titre="À traiter"  valeur={String(compteurs.soumises)}     detail="demandes reçues" />
            <Tuile titre="Urgentes"   valeur={String(compteurs.urgentes)}     detail="chantier à l'arrêt" alerte={compteurs.urgentes > 0} />
            <Tuile titre="En cours"   valeur={String(compteurs.enTraitement)} detail="prises en main" />
          </div>
        )}

        <div className="ds-filters">
          <select className="ds-fi" style={{ width: 'auto' }} value={statut}
                  onChange={(e) => setStatut(e.target.value as StatutDemande | '')}>
            <option value="">En attente (à traiter et en cours)</option>
            {(Object.keys(LIBELLE_STATUT) as StatutDemande[]).map((s) => (
              <option key={s} value={s}>{LIBELLE_STATUT[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Demandes</div>
              <div className="ds-table-sub">
                {lignes.length} demande{lignes.length > 1 ? 's' : ''} — les urgentes en premier
              </div>
            </div>
          </div>
          {/* 11/10/2026 : sur un téléphone, les colonnes « État » et « Action » sortaient de l'écran — le tableau défile. */}
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="ds-tbl" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th style={{ width: 130 }}>Référence</th>
                <th style={{ width: 180 }}>Demandeur</th>
                <th>Besoin</th>
                <th style={{ width: 120 }}>Reçue</th>
                <th style={{ width: 100 }}>État</th>
                <th style={{ width: 180 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
              {!isLoading && lignes.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 48 }}>
                  <Inbox size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune demande en attente</p>
                </td></tr>
              )}
              {!isLoading && lignes.map((d) => (
                <tr key={d.id}>
                  <td>
                    <span className="ds-ref-code">{d.reference}</span>
                    {d.urgence === 'urgente' && (
                      <div style={{ marginTop: 3 }}>
                        <span className="ds-bd ds-bd-d" style={{ fontSize: 10 }}>URGENT</span>
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    <div className="ds-entry-label">{d.demandeurNom}</div>
                    <div style={{ color: 'var(--tx-3)' }}>{d.origine === 'terrain' ? 'portail terrain' : 'bureau'}</div>
                  </td>
                  <td style={{ fontSize: 11 }}>
                    <div style={{ fontStyle: 'italic', color: 'var(--tx-2)', marginBottom: 3 }}>« {d.motif} »</div>
                    {d.lignes.map((l) => (
                      <div key={l.id}>• {l.designation} — {Number(l.quantite)} {l.unite}</div>
                    ))}
                    {d.motifRefus && (
                      <div style={{ color: 'var(--danger)', marginTop: 3 }}>Refus : {d.motifRefus}</div>
                    )}
                  </td>
                  <td style={{ fontSize: 11 }}>{formatDateTimeShort(d.createdAt)}</td>
                  <td>
                    <span className={`ds-bd ${COULEUR_STATUT[d.statut]}`} style={{ fontSize: 10 }}>
                      {LIBELLE_STATUT[d.statut]}
                    </span>
                  </td>
                  <td>
                    {(d.statut === 'soumise' || d.statut === 'en_traitement') && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setATraiter(d)}>
                          Chiffrer <ArrowRight size={11} />
                        </button>
                        <button className="ds-btn ds-btn-g ds-btn-sm" style={{ color: 'var(--danger)' }}
                                onClick={() => void refuserDemande(d)}>
                          Refuser
                        </button>
                      </div>
                    )}
                    {d.statut === 'convertie' && (
                      <span style={{ fontSize: 11, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircle2 size={12} /> bon de commande créé
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <DialogConversion
        demande={aTraiter}
        onClose={() => setATraiter(null)}
        onConverti={(ref, refDemande) => {
          setMessage(`${refDemande} convertie — bon de commande ${ref} créé en brouillon. Complétez-le puis soumettez-le au DAF.`);
          setATraiter(null);
        }}
      />
    </>
  );
};

const Tuile = ({ titre, valeur, detail, alerte }: { titre: string; valeur: string; detail: string; alerte?: boolean }) => (
  <div className="ds-kpi-card">
    <div className="ds-kpi-label">{titre}</div>
    <div className="ds-kpi-value" style={alerte ? { color: 'var(--danger)' } : undefined}>{valeur}</div>
    <div className="ds-kpi-detail">{detail}</div>
  </div>
);

// ── Chiffrage et conversion ──────────────────────────────────────────────

const DialogConversion = ({
  demande, onClose, onConverti,
}: {
  demande: Demande | null;
  onClose: () => void;
  onConverti: (reference: string, refDemande: string) => void;
}) => {
  const qc = useQueryClient();
  const { data: fournisseursData } = useFournisseurs({ actif: true, limit: 200 });
  const [fournisseurId, setFournisseurId] = useState('');
  const [prix, setPrix] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);

  const convertir = useMutation({
    mutationFn: (body: { fournisseurId: string; prix: Array<{ ligneId: string; prixUnitaireCents: number }> }) =>
      demandesApi.convertir(demande!.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['achats'] }),
  });

  if (!demande) return null;

  const fournisseurs = fournisseursData?.data ?? [];
  const lignesChiffrees = demande.lignes.filter((l) => Number(prix[l.id]) > 0);
  const total = lignesChiffrees.reduce(
    (s, l) => s + Math.round(Number(prix[l.id]) * 100) * Number(l.quantite), 0,
  );

  const valider = async () => {
    setErreur(null);
    if (!fournisseurId) { setErreur('Choisissez le fournisseur.'); return; }
    if (lignesChiffrees.length === 0) { setErreur('Donnez un prix à au moins une ligne.'); return; }
    try {
      const bca = await convertir.mutateAsync({
        fournisseurId,
        prix: lignesChiffrees.map((l) => ({
          ligneId:           l.id,
          // L'acheteur saisit des francs ; la plateforme compte en centimes.
          prixUnitaireCents: Math.round(Number(prix[l.id]) * 100),
        })),
      });
      setPrix({}); setFournisseurId('');
      onConverti(bca.reference, bca.demandeReference);
    } catch (e) {
      setErreur(extractError(e).message);
    }
  };

  return (
    <Dialog open={!!demande} onClose={onClose} title={`${demande.reference} — chiffrer et commander`}>
      <div style={{ fontSize: 12, color: 'var(--tx-2)', fontStyle: 'italic', marginBottom: 14 }}>
        « {demande.motif} » — {demande.demandeurNom}
      </div>

      {erreur && (
        <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 12 }}>
          <div className="ds-alert-txt">{erreur}</div>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Fournisseur</label>
        <select className="ds-fi" style={{ width: '100%' }} value={fournisseurId}
                onChange={(e) => setFournisseurId(e.target.value)}>
          <option value="">— choisir —</option>
          {fournisseurs.map((f: { id: string; raisonSociale: string }) => (
            <option key={f.id} value={f.id}>{f.raisonSociale}</option>
          ))}
        </select>
      </div>

      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: 12 }}>
      <table className="ds-tbl" style={{ minWidth: 520 }}>
        <thead>
          <tr>
            <th>Article demandé</th>
            <th style={{ width: 90 }}>Quantité</th>
            <th style={{ width: 130 }}>Prix unitaire</th>
            <th style={{ width: 110, textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {demande.lignes.map((l) => {
            const pu = Number(prix[l.id]);
            return (
              <tr key={l.id}>
                <td style={{ fontSize: 12 }}>
                  <div className="ds-entry-label">{l.designation}</div>
                  <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>
                    {l.nature === 'immobilisation' ? 'matériel durable — ira au patrimoine' : l.nature.replace('_', ' ')}
                  </div>
                </td>
                <td style={{ fontSize: 12 }}>{Number(l.quantite)} {l.unite}</td>
                <td>
                  <input className="ds-fi" type="number" min={0} placeholder="FCFA"
                         style={{ width: '100%' }}
                         value={prix[l.id] ?? ''}
                         onChange={(e) => setPrix((p) => ({ ...p, [l.id]: e.target.value }))} />
                </td>
                <td style={{ textAlign: 'right', fontSize: 12 }}>
                  {pu > 0 ? formatMontant(Math.round(pu * 100) * Number(l.quantite)) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      <p style={{ fontSize: 11, color: 'var(--tx-3)', marginBottom: 12 }}>
        Une ligne laissée sans prix n'entre pas dans le bon de commande — utile si le besoin
        est déjà couvert par le stock.
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--bd)', paddingTop: 12 }}>
        <div style={{ fontSize: 13 }}>
          Total HT : <strong>{formatMontant(total)} FCFA</strong>
          <span style={{ color: 'var(--tx-3)' }}> — {lignesChiffrees.length}/{demande.lignes.length} ligne(s)</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={() => void valider()} disabled={convertir.isPending}>
            {convertir.isPending ? 'Création…' : 'Créer le bon de commande'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
